import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { AuthShell } from "@/pages/Login";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/api/auth/forgot-password", { email });
      setSent(data.dev_token || "sent");
      toast.success("Reset link generated");
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <AuthShell
      title="Reset your password"
      subtitle="Enter your email and we will generate a reset link."
      footer={<Link to="/login" className="text-emerald-800">Back to login</Link>}
    >
      {sent ? (
        <div className="space-y-4" data-testid="forgot-sent-panel">
          <p className="text-sm text-slate-600">
            If an account exists, a reset link has been created. Email delivery is mocked in Stage 1.
          </p>
          {sent !== "sent" && (
            <Button asChild className="w-full rounded-full bg-emerald-900">
              <Link to={`/reset-password?token=${sent}`} data-testid="forgot-continue-btn">Continue to reset</Link>
            </Button>
          )}
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="fp-email">Email address</Label>
            <Input id="fp-email" type="email" required className="mt-1.5" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email-input" />
          </div>
          <Button type="submit" className="w-full rounded-full bg-emerald-900 py-6" data-testid="forgot-submit-btn">
            Send reset link
          </Button>
        </form>
      )}
    </AuthShell>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const [password, setPassword] = useState("");
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/api/auth/reset-password", { token: params.get("token"), password });
      toast.success("Password updated. Please log in.");
      navigate("/login");
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <AuthShell title="Choose a new password" subtitle="Your reset link is valid for one hour.">
      <form onSubmit={submit} className="space-y-4">
        <div>
          <Label htmlFor="rp-password">New password</Label>
          <PasswordInput id="rp-password" minLength={8} required className="mt-1.5" value={password} onChange={(e) => setPassword(e.target.value)} testId="reset-password-input" />
        </div>
        <Button type="submit" className="w-full rounded-full bg-emerald-900 py-6" data-testid="reset-submit-btn">
          Update password
        </Button>
      </form>
    </AuthShell>
  );
}
